import { TimetableReportType } from '@bp/planung-graphql-types';
import {
  AddIcon,
  Button,
  ButtonGroup,
  DeleteIcon,
  Dropdown,
  DropdownMenu,
  ImportIcon,
  Table,
  TableColumns,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryExecute } from 'urql';
import { useConfirm } from '../../hooks/useConfirm';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import {
  createTimetableCollectiveReport,
  createTimetableSingleReport,
  deleteReport,
  downloadDebug,
  downloadReport,
} from '../../utils/pdfReport';
import { ReportType } from './types';

export type TimetableReportsTableType = {
  uuid: string;
  name: string;
  contextUuid: string;
  contextName: string;
  timestamp: dayjs.Dayjs;
};

type TimetableReportsTableProps = {
  contextUuid: string;
  tableData: TimetableReportsTableType[];
  refetchTableData: UseQueryExecute;
  type: ReportType;
  subType: TimetableReportType;
  onLoad: (loading: boolean) => void;
};

export const TimetableReportsTable = observer(
  ({ contextUuid, type, subType, onLoad, tableData, refetchTableData }: TimetableReportsTableProps) => {
    const { t } = useTranslation();
    const permissionChecker = usePermissionChecker();

    const [loading, setLoading] = useState<string | 'all' | null>(null);

    const { ConfirmationDialog, confirm } = useConfirm({
      defaultMessage: t('delete.message', {
        context: 'female',
        count: 1,
        type: t('pdf.title', { count: 1 }),
      }),
    });

    const isSingle =
      subType === TimetableReportType.SingleClass ||
      subType === TimetableReportType.SingleRoom ||
      subType === TimetableReportType.SingleSubject ||
      subType === TimetableReportType.SingleTeacher;

    const columns: TableColumns<TimetableReportsTableType>[] = [
      {
        header: t('common.date'),
        id: 'name',
        accessorKey: 'name',
        size: isSingle ? 350 : 450,
      },
      ...(isSingle
        ? [
            {
              header: t('common.type'),
              id: 'type',
              accessorKey: 'type',
              size: 90,
            },
          ]
        : []),
      {
        header: '',
        id: 'timestamp',
        accessorFn: (row) => {
          return row.timestamp.format('DD.MM.YY HH:mm');
        },
        size: 120,
      },
    ];

    async function handleDownload(report: TimetableReportsTableType) {
      if (loading) return;
      setLoading(report.uuid);
      onLoad(true);
      await downloadReport(report, subType);
      setLoading(null);
      onLoad(false);
    }

    async function handleDelete(report: TimetableReportsTableType) {
      if (loading) return;
      const result = await confirm();
      if (!result) return;
      setLoading(report.uuid);
      onLoad(true);
      await deleteReport(report, subType);
      refetchTableData({ requestPolicy: 'network-only' });
      setLoading(null);
      onLoad(false);
    }

    async function handleGenerateAll() {
      if (loading) return;
      setLoading('all');
      onLoad(true);
      await createTimetableCollectiveReport(contextUuid, type, subType);
      refetchTableData({ requestPolicy: 'network-only' });
      setLoading(null);
      onLoad(false);
    }

    async function handleGenerateSingle() {
      if (loading) return;
      setLoading('all');
      onLoad(true);
      await createTimetableSingleReport(contextUuid, type, subType);
      refetchTableData({ requestPolicy: 'network-only' });
      setLoading(null);
      onLoad(false);
    }

    async function handleDebug() {
      if (loading) return;
      setLoading('all');
      onLoad(true);
      await downloadDebug(contextUuid, type);
      setLoading(null);
      onLoad(false);
    }

    const actions = () =>
      isSingle ? (
        <>
          {permissionChecker?.isOmniAdmin() && (
            <Button hierarchy={'secondary'} isLoading={loading === 'all'} onClick={handleDebug}>
              Download Debug
            </Button>
          )}
          <Dropdown
            disabled={loading === 'all'}
            trigger={
              <Button isLoading={loading === 'all'} hierarchy='primary' icon={<AddIcon />} title={t('common.generate')}>
                {t('common.generate')}
              </Button>
            }
          >
            <DropdownMenu
              data={[
                { label: t('pdf.collectivePdf'), onClick: handleGenerateAll },
                { label: t('pdf.singlePdf'), onClick: handleGenerateSingle },
              ]}
            />
          </Dropdown>
        </>
      ) : (
        <Button
          isLoading={loading === 'all'}
          hierarchy='primary'
          icon={<AddIcon />}
          title={t('common.generate')}
          onClick={handleGenerateAll}
        >
          {t('common.generate')}
        </Button>
      );

    return (
      <>
        <Table<TimetableReportsTableType>
          className='mb-9'
          data={tableData.filter((d) => d.contextUuid === contextUuid)}
          columns={columns}
          showActionBar
          hideHeader
          customPadding='var(--spacing-6)'
          actionBarSettings={{
            globalFilterPlaceholder: t('common.search'),
            extendedActionsRight: actions(),
          }}
          emptyStateSettings={{
            size: 'small',
            hideIcon: true,
            subtitle: '',
            title: t('pdf.noPdfFound'),
          }}
          lastColWidth='75px'
          lastCol={({ original }) => (
            <ButtonGroup loading={loading === original.uuid}>
              <Button
                hierarchy='secondary'
                icon={<ImportIcon className='small' />}
                onClick={() => handleDownload(original)}
              />
              <Button
                disabled={!permissionChecker?.canWriteTimetable()}
                hierarchy='secondary'
                icon={<DeleteIcon className='small' />}
                onClick={() => handleDelete(original)}
              />
            </ButtonGroup>
          )}
        />

        <ConfirmationDialog />
      </>
    );
  },
);
